import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { Listbox } from 'tio-ui/components/listbox';
import Popover from 'tio-ui/components/popover';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Button from 'tio-ui/components/button';
import { Header, Section, VStack } from 'tio-ui/components/layout';
import { eq, gt } from 'tio-ui/utilities';
import { PhCaretDown } from 'ember-phosphor-icons';
import { LinkTo } from '@ember/routing';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import AnnualBenefits from 'tio-common/components/tas/annual-benefits';
import ApplicationRow from 'tio-employee/components/tas/dashboard/application-row';
import BenefitCard from 'tio-employee/components/tas/dashboard/benefit-card';
import ProgramInstanceCard from 'tio-employee/components/tas/dashboard/program-instance-card';
import RecentApplicationsEmpty from 'tio-employee/components/tas/dashboard/recent-applications-empty';
import type { TasProgramTemplateExtension as TasProgramTemplate } from 'tio-data/data/resources/tas-program-template.ext';
import type { IntlService } from 'ember-intl';
import type {
  RecentApplication,
  ProgramInstance,
} from 'tio-common/utils/tuition-assistance/application-states';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';
import type SessionContextService from 'tio-employee/services/session-context';

interface UnifiedRecentItem {
  type: 'application' | 'instance';
  updatedAt: string;
  application?: RecentApplication;
  instance?: ProgramInstance;
}

interface DashboardV3Signature {
  Args: {
    model: {
      templates: TasProgramTemplate[];
      recentApplications: RecentApplication[];
      programInstances: ProgramInstance[];
      enrolledTemplateIds: Set<string>;
      isHeldParticipant: boolean;
      limitsStatus: LimitsStatusDataSignature;
    };
  };
}

type SortOption = 'alphabetical' | 'date';

export default class TasDashboardV3 extends Component<DashboardV3Signature> {
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;

  @tracked currentSort: SortOption = 'date';

  sortOptions: SortOption[] = ['alphabetical', 'date'];

  get companySettings() {
    return this.sessionContext.currentEmployee?.company.companySetting;
  }

  get sortLabel() {
    return this.currentSort === 'alphabetical' ? 'alphabetically' : 'by date';
  }

  get sortedTemplates() {
    const templates = [...this.args.model.templates];

    if (this.currentSort === 'alphabetical') {
      return templates.sort((a, b) => (a.programName || '').localeCompare(b.programName || ''));
    }

    // Sort by date (newest first)
    return templates.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
  }

  get hasRecentApplications() {
    return (
      this.args.model.recentApplications.length > 0 || this.args.model.programInstances.length > 0
    );
  }

  get isDependentProgramsEnabled() {
    return (
      this.sessionContext.currentEmployee?.company.companySetting.tuitionAssistance
        ?.isDependentProgramsEnabled || false
    );
  }

  /**
   * Interleave Type 1/2/3 applications with Type 4 instances, sorted by most recent activity
   */
  get sortedRecentItems(): UnifiedRecentItem[] {
    const items: UnifiedRecentItem[] = [];

    // Add flat applications (Type 1/2/3)
    for (const app of this.args.model.recentApplications) {
      items.push({
        type: 'application',
        updatedAt: app.updatedAt || '',
        application: app,
      });
    }

    // Add Type 1 and Type 4.2/4.3 instances
    for (const instance of this.args.model.programInstances) {
      items.push({
        type: 'instance',
        updatedAt: instance.instanceApplication.updatedAt || '',
        instance,
      });
    }

    // Sort all by updatedAt descending (most recent first)
    return items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  @tracked sortPopoverOpen = false;

  openSortPopover = () => {
    this.sortPopoverOpen = true;
  };

  closeSortPopover = () => {
    this.sortPopoverOpen = false;
  };

  handleSortSelect = (key: string) => {
    this.currentSort = key as SortOption;
    this.closeSortPopover();
  };

  getOptionLabel = (option: string) => {
    return option === 'alphabetical' ? 'Alphabetically' : 'By Date';
  };

  isEnrolledInTemplate = (template: TasProgramTemplate) => {
    return template.id ? this.args.model.enrolledTemplateIds.has(template.id) : false;
  };

  <template>
    <VStack>
      {{! Annual Benefits Section }}
      {{#unless this.companySettings.tuitionAssistance.hideAnnualBenefitsCard}}
        <Section>
          <:header>
            <div class="tio-row-between">
              <Header>{{t "tas.dashboard.annual_benefits.title"}}</Header>
              <LinkTo
                @route="authenticated.tas.benefit-history"
                class="tio-copy tio-text-sm-semibold"
                data-test-annual-benefits-view-history-link
              >
                {{t "tas.benefit_history.view_history"}}
              </LinkTo>
            </div>
          </:header>
          <:body>
            <AnnualBenefits
              @limitsStatus={{@model.limitsStatus}}
              @employee={{this.sessionContext.currentEmployee}}
            />
          </:body>
        </Section>
      {{/unless}}
      {{! Recent Applications Section }}
      <Section>
        <:header>
          <Header>{{t "tas.dashboard.recent_applications"}}</Header>
        </:header>
        <:actions>
          {{#if this.isDependentProgramsEnabled}}
            <Button
              @intent="primary"
              @appearance="outlined"
              class="text-sm"
              {{on "click" (transitionTo "authenticated.tas.dependent-programs")}}
              data-test-dependent-programs-button
            >
              {{t "common.tuition_assistance.dependent_programs_button"}}
            </Button>
          {{else}}
            <LinkTo
              @route="authenticated.tas.benefit-history"
              class="tio-copy tio-text-sm-semibold"
              data-test-view-history-link
            >
              {{t "tas.benefit_history.view_history"}}
            </LinkTo>
          {{/if}}
        </:actions>
        <:body>
          {{#if this.hasRecentApplications}}
            <div class="tio-col-tight">
              {{#each this.sortedRecentItems as |item|}}
                {{#if (eq item.type "instance")}}
                  {{! Type 4 - Instance Card (expandable only if has children) }}
                  {{#let item.instance as |instance|}}
                    {{#if instance}}
                      <ProgramInstanceCard
                        @instance={{instance}}
                        @isExpandable={{gt instance.childApplications.length 0}}
                        @defaultExpanded={{true}}
                      />
                    {{/if}}
                  {{/let}}
                {{else}}
                  {{! Type 1/2/3 - Flat Application Card }}
                  {{#let item.application as |application|}}
                    {{#if application}}
                      <ApplicationRow @application={{application}} />
                    {{/if}}
                  {{/let}}
                {{/if}}
              {{/each}}
            </div>
          {{else}}
            <RecentApplicationsEmpty />
          {{/if}}
        </:body>
      </Section>

      {{! Eligible Benefits Section }}
      <Section>
        <:header>
          <Header>{{t "tas.dashboard.eligible_benefits"}}</Header>
        </:header>
        <:actions>
          <Popover
            @placement="bottom"
            @isOpen={{this.sortPopoverOpen}}
            @onOpen={{this.openSortPopover}}
            @onClose={{this.closeSortPopover}}
          >
            <:trigger as |pop|>
              <button
                type="button"
                class="tio-row-snug px-2 py-1.5 tio-text-xs-medium tio-fg-muted border tio-border-default rounded-lg hover:tio-bg-sunken"
                {{pop.register}}
                {{on "click" pop.toggle}}
              >
                {{t "tas.dashboard.sort"}}
                {{this.sortLabel}}
                <PhCaretDown @size={{12}} />
              </button>
            </:trigger>
            <:body>
              <Listbox
                @isKeyboardEventsEnabled={{true}}
                @allowEmpty={{true}}
                @selectedKeys={{array this.currentSort}}
                @onAction={{this.handleSortSelect}}
                @intent="primary"
                @selectionMode="single"
                @class="w-full overflow-hidden"
                as |l|
              >
                {{#each this.sortOptions as |option|}}
                  <l.Item class="[&>span]:text-sm" @key={{option}}>
                    {{this.getOptionLabel option}}
                  </l.Item>
                {{/each}}
              </Listbox>
            </:body>
          </Popover>
        </:actions>
        <:body>
          {{! Benefits Card Grid - 3 columns }}
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {{#each this.sortedTemplates as |template|}}
              <BenefitCard
                @template={{template}}
                @isEnrolled={{this.isEnrolledInTemplate template}}
              />
            {{/each}}
          </div>
        </:body>
      </Section>
    </VStack>
  </template>
}
